import { validateDropdownList } from './dropdownListValidator'
import { ValidationFeedback } from './listValidator'
import * as ListDataManager from '../../listDataManager'
import {
  FieldId,
  ListGovernance,
  ValidationTemplateField,
  ValidationValue,
} from '../validationTypes'
import { t } from '@lingui/core/macro'

/**
 * This validates the cells for a dependent list. If the cell is the parent dependent list cell,
 * normal list validation is applied, otherwise the cell's value is rebuilt based upon the
 * selection from the parent cell. This function mutates the validationErrors object to indicate
 * if there were errors.
 */
export function validateDependentList(
  value: string | undefined,
  rowData: Record<FieldId, ValidationValue>,
  listGovernance: ListGovernance | null,
  dependentFields: ValidationTemplateField[] | undefined,
  feedback: ValidationFeedback,
) {
  if (!value) {
    return
  }

  if (!listGovernance) {
    feedback.errors.push(t`Column is misconfigured`)
    return
  }

  if (dependentFields?.length) {
    validateDependentCells(dependentFields, rowData, feedback)
  }

  const { list_id, list_column_id } = listGovernance
  const isFirstColumn = listGovernance.list_column && listGovernance.list_column.precedence === 0

  if (isFirstColumn) {
    const validListValues = ListDataManager.getUniqueValuesForListColumn(list_id, list_column_id)
    validateDropdownList(value, listGovernance, validListValues, feedback)
  }

  const parentColumn = listGovernance.meta.allColumnsInList[0]

  if (!parentColumn) {
    return
  }

  const parentGovernanceOwner = getGovernanceOwner(parentColumn)
  const parentGovernance = parentGovernanceOwner.list_governance
  if (!parentGovernance) {
    return
  }

  const parentDelimiter = parentGovernance.multi === 1 ? parentGovernance.delimiter : null
  const parentValue = rowData[parentColumn.field_id]?.cell_value
  const parentColumnId = parentGovernance.list_column_id
  const currentCellListColumnId = listGovernance.list_column_id
  const selectedParentValues = parentDelimiter
    ? (parentValue?.split(parentDelimiter).sort() ?? [])
    : [parentValue ?? '']
  const rowsByKey = ListDataManager.getRowsIndexedByColumnValue(list_id, parentColumnId)
  const selectedListRows = selectedParentValues.map(key => rowsByKey[key]).filter(x => !!x)
  const parentValueToChildValueList = selectedListRows.map(row => row[currentCellListColumnId])

  if (
    !isFirstColumn &&
    !parentValueToChildValueList.includes(value) &&
    valueNotFoundInList({ del: parentDelimiter, list: parentValueToChildValueList, value })
  ) {
    if (parentValue) {
      feedback.warnings.push(
        t`The following values are not in the pick list<br />associated to ${parentValue}: ${value}`,
      )
    }
  }
}

const getGovernanceOwner = (templateField: ValidationTemplateField) => {
  const isVariable = templateField.field.segment_type === 'variable'
  return isVariable ? templateField : templateField.field
}

function validateDependentCells(
  dependentFields: ValidationTemplateField[],
  rowData: Record<FieldId, ValidationValue>,
  feedback: ValidationFeedback,
) {
  dependentFields.forEach(templateField => {
    const { field_id: depFieldId } = templateField
    const { cell_value: depCellValue } = rowData[depFieldId] ?? {}
    const depGovOwner = getGovernanceOwner(templateField)
    const depListGovernance = depGovOwner.list_governance
    if (!depListGovernance) {
      return
    }
    const { list_column_id: listColumnId } = depListGovernance
    if (listColumnId) {
      validateDropdownList(depCellValue, depListGovernance, null, feedback)
    }
  })
}

/**
 * Check if a value exists in a list of values.
 * - Used for multi-select lists.
 */
function valueNotFoundInList({
  del,
  list,
  value,
}: {
  del: string | null
  list: unknown[]
  value: string
}) {
  if (!del) {
    return !list.includes(value)
  }

  return value.split(del).some(v => !list.includes(v))
}
